<template>
  <div>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="mb-1 d-flex align-items-center justify-content-start mb-md-0"
        >
          <label>Show</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="mr-1 per-page-selector d-inline-block ml-50"
          />
          <b-button v-tracker="'button:request-pcc'" v-b-modal.request-modal variant="primary">
            Request PCC
          </b-button>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          class="mb-1 d-flex align-items-center justify-content-end mb-md-0"
        >
          <label class="mr-1">
            Search
          </label>
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="mr-1 d-inline-block"
              placeholder="Search..."
              v-model="searchWord"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <!-- Larger screen than mobile -->
    <div class="d-none d-md-flex">
      <b-table
        class="w-100 border"
        responsive
        striped
        :items="pcc"
        :fields="pccFields"
      />
    </div>
    <!-- Mobile screen -->
    <div class="d-flex d-md-none flex-column">
      <!-- Loading -->
      <div v-if="isBusy" class="text-center text-danger my-2">
        <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
        <strong class="text-secondary">Loading...</strong>
      </div>
      <!-- Data -->
      <div v-if="!isBusy && pcc.length == 0" class="text-center text-danger my-2">
        <strong class="text-secondary">No data</strong>
      </div>
      <b-row
        v-if="!isBusy && pcc.length > 0"
        v-for="(item, index) in pcc"
        :key="item.id"
        class="mb-3 list-item"
      >
        <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
          <strong class="flex-grow-1 truncate">{{ item.product.PRODUCT }}</strong>
        </b-col>
        <b-col cols="12" class="d-flex flex-wrap">
          <div style="font-size: 0.75rem;">
            <p><strong>PORTFOLIO:</strong> {{ item.product.category != null ? item.product.category.name : '-' }}</p>
            <p><strong>REQUESTED:</strong> {{ Number(item.total).toFixed(0) }}</p>
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="px-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ pagination.from }} to {{ pagination.to }} of
            {{ pagination.total }} entries
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
            @input="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
    <b-modal
      title="Request PCC"
      hide-footer
      id="request-modal"
      centered
      hide-header-close
      no-close-on-backdrop
    >
      <b-form @submit.prevent="addRequest">
        <b-form-group label-for="product">
          <template slot="label">
            <span>Product <strong class="text-danger">*</strong></span>
          </template>
          <v-select
            v-model="selectedProducts"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            multiple
            label="PRODUCT"
            :options="products"
          />
        </b-form-group>
        <b-form-group class="d-flex justify-content-end">
          <b-button
            v-tracker="'button:request-pcc:cancel'"
            type="button"
            variant="light"
            class="mr-1"
            @click="$bvModal.hide('request-modal')"
            :disabled="form.isLoading"
          >
            Cancel
          </b-button>
          <b-button v-tracker="'button:request-pcc:request'" type="submit" variant="primary" :disabled="form.isLoading">
            Request
          </b-button>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import VSelect from 'vue-select'
import {
  BSpinner,
  BRow,
  BCol,
  BTable,
  BFormCheckbox,
  BButton,
  BFormInput,
  BPagination,
  VBModal,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BSpinner,
    BTable,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    VSelect,
    BFormInput,
    BPagination,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
  },

  directives: {
    Ripple,
    VBModal,
  },

  data() {
    return {
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      pcc: [],
      pccFields: [
        {
          key: 'product.PRODUCT',
          label: 'Title',
          sortable: true,
        },
        {
          key: 'product.category',
          formatter: (val, key, item) => {
            return val ? val.name : ''
          },
          label: 'Portfolio',
          sortable: true,
        },
        {
          key: 'total',
          formatter: (val, key, item) => {
            return Number(val).toFixed(0)
          },
          label: 'Requested',
          sortable: true,
        },
      ],
      searchWord: '',
      products: [],
      form: { isLoading: false },
      selectedProducts: [],
    }
  },

  watch: {
    perPage() {
      this.fetchPcc()
    },
    currentPage() {
      this.fetchPcc()
    },
    searchWord(newVal) {
      this.searchByTitle()
    },
  },

  mounted() {
    this.fetchPcc()
    this.getProductDropdown()
  },

  methods: {
    fetchPcc() {
      this.isBusy = true

      this.$http
        .get('/pcc/request-portal', {
          params: {
            page: this.currentPage,
            perPage: this.perPage,
            s: this.searchWord.toString().trim(),
          },
        })
        .then(({ data }) => {
          this.currentPage = data.current_page
          this.pagination.total = data.total
          this.pagination.from = data.from
          this.pagination.to = data.to
          this.pcc = data.data
        })
        .finally(() => {
          this.isBusy = false
        })
    },

    searchByTitle: debounce(function() {
      this.fetchPcc()
    }, 200),

    changePage(page) {
      this.currentPage = page
    },

    async getProductDropdown() {
      return await this.$http.get(`/product/index`)
        .then(res => {
          this.products = res.data.data
        })
    },

    addRequest() {
      this.form.isLoading = true

      this.$http
        .post('/pcc/request', {
          products_kode: this.selectedProducts.map(prod => prod.KODE),
        })
        .then(res => {
          console.log(res)
          this.$notif(
            'Success',
            `PCC ${res.data.toString()} has been successfully requested.`,
            'success',
            'InfoIcon',
            5000
          )
          this.$bvModal.hide('request-modal')
          this.selectedProducts = []
          this.fetchPcc()
        })
        .catch(({ data }) => {
          this.$notif('Error', `${data}`, 'error')
        })
        .finally(() => (this.form.isLoading = false))
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
